import { Injectable, signal } from '@angular/core';

/**
 * Speelt subtiele feedback-tonen voor de Human Design-spelmodi af zonder
 * externe audio-bestanden: alles wordt op de fly gesynthetiseerd met de
 * Web Audio API. Eén gedeelde {@link AudioContext} per service-instantie
 * (lazy aangemaakt bij de eerste play) houdt het geheugengebruik laag en
 * vermijdt browser-deprecation rond te veel parallelle contexts.
 *
 * Beide tonen zijn bewust kort (~150 ms) en zacht (gain ≤ 0.12) zodat ze
 * niet hinderlijk worden bij snelle opeenvolging in flow-states.
 */
@Injectable({ providedIn: 'root' })
export class HdGameFeedbackAudioService {
  private audioContext: AudioContext | null = null;

  /**
   * Globale aan/uit-vlag voor feedback-geluiden in alle Human Design-spelmodi.
   * Standaard `true`; via de audio-toggle in de spel-paneelheader kan de
   * speler de geluiden uitschakelen — alle `playCorrect`/`playWrong`-aanroepen
   * worden dan stil doorgegeven.
   */
  private readonly _enabled = signal(true);
  readonly enabled = this._enabled.asReadonly();

  toggleEnabled(): void {
    this._enabled.update(v => !v);
  }

  /**
   * Hoge, korte twee-tonen "ping" (E5 → B5) die als positieve bevestiging
   * leest. Wordt gespeeld bij een correct antwoord in Poortenjacht of
   * Kanalenjacht (memory- of klik-flow).
   */
  playCorrect(): void {
    if (!this._enabled()) {
      return;
    }
    const ctx = this.ensureContext();
    if (ctx == null) {
      return;
    }
    const now = ctx.currentTime;
    this.playTone(ctx, 660, now, 0.08, 0.1);
    this.playTone(ctx, 990, now + 0.08, 0.12, 0.1);
  }

  /**
   * Lage, korte "fout" — twee dalende tonen (G4 → D4) met een zachte zaagtand
   * voor een herkenbare maar niet vervelende negatieve cue.
   */
  playWrong(): void {
    if (!this._enabled()) {
      return;
    }
    const ctx = this.ensureContext();
    if (ctx == null) {
      return;
    }
    const now = ctx.currentTime;
    this.playTone(ctx, 392, now, 0.1, 0.12, 'triangle');
    this.playTone(ctx, 294, now + 0.1, 0.15, 0.12, 'triangle');
  }

  private playTone(
    ctx: AudioContext,
    frequency: number,
    startTime: number,
    duration: number,
    peakGain: number,
    type: OscillatorType = 'sine',
  ): void {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, startTime);

    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(peakGain, startTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.02);
  }

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') {
      return null;
    }
    if (this.audioContext == null) {
      const AudioContextCtor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioContextCtor == null) {
        return null;
      }
      try {
        this.audioContext = new AudioContextCtor();
      } catch {
        return null;
      }
    }
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch(() => {});
    }
    return this.audioContext;
  }
}
